// /partners — list of DSSAB service divisions + Sault Ste. Marie Housing Corp.
//
// Five partners, fixed (no create). Each row shows mandate one-liner, ceiling,
// committed match, available, project count, status. Edit opens a right-slide
// drawer with all fields. Save is one-click (⌘⏎). No multi-step modal.
//
// Density target: 5 partners visible without scroll at typical laptop heights.
// Empty state: not applicable.
// Loading: "Loading partners — a moment."
// Errors: recovery instructions inline.

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api';
import {
  formatCAD,
  formatCADCompact,
  exposureStatusMeta,
  moneyToneCls,
} from '../lib/format';
import { pushToast } from '../hooks/useToasts';
import { PageFooter } from '../components/PageFooter';

// ── Types ────────────────────────────────────────────────────────────────

interface Partner {
  id: string;
  slug: string;
  name: string;
  partner_type: 'dssab_division' | 'housing_corp' | 'community_partner';
  mandate: string | null;
  service_area: string[];
  annual_budget_cad: number | null;
  sectors: string[];
  keywords: string[];
  cra_business_number: string | null;
  indigenous_led: boolean;
  geographic_catchment: string[];
  matching_funds_ceiling: number | null;
  avoid_funders: string[];
  fiscal_year_start_month: number;
  created_at: number;
  updated_at: number;
}

interface FinanceExposure {
  total_committed_in_progress_cad: number;
  total_open_applications: number;
  profiles: Array<{
    profile_id: string;
    profile_slug: string;
    matching_funds_ceiling: number | null;
    committed_in_progress_cad: number;
    open_application_count: number;
    utilization_pct: number | null;
    status: 'green' | 'amber' | 'red' | 'unset';
  }>;
}

interface ProjectListRow {
  id: string;
  partner_profile_id: string;
}

// ── Page ─────────────────────────────────────────────────────────────────

export default function PartnersPage() {
  const navigate = useNavigate();
  const [openId, setOpenId] = useState<string | null>(null);

  const partners = useQuery({
    queryKey: ['partners'],
    queryFn: () => api.get<Partner[]>('/partners'),
  });
  const finance = useQuery({
    queryKey: ['finance-exposure'],
    queryFn: () => api.get<FinanceExposure>('/dashboards/finance-exposure'),
  });
  const projects = useQuery({
    queryKey: ['projects', 'all'],
    queryFn: () => api.get<ProjectListRow[]>('/projects'),
  });

  if (partners.error instanceof ApiError && partners.error.status === 401) {
    navigate({ to: '/welcome' });
    return null;
  }

  const rows = partners.data?.data ?? [];
  const financeByPartner = new Map(
    (finance.data?.data.profiles ?? []).map((p) => [p.profile_id, p]),
  );
  const projectCountByPartner = new Map<string, number>();
  for (const p of projects.data?.data ?? []) {
    projectCountByPartner.set(
      p.partner_profile_id,
      (projectCountByPartner.get(p.partner_profile_id) ?? 0) + 1,
    );
  }

  const openPartner = rows.find((r) => r.id === openId);

  return (
    <div className="h-full flex flex-col min-h-0">
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-[1200px] mx-auto px-5 py-5">
          <header className="mb-4">
            <h1 className="text-lg font-semibold text-neutral-900">Partners</h1>
            <p className="mt-1 text-xs text-neutral-600">
              The five entities Granted matches grants against: four DSSAB service divisions and the Sault Ste. Marie Housing Corporation. Edit a partner to update its mandate, sectors, keywords, or matching-funds ceiling.
            </p>
          </header>

          {partners.isLoading && (
            <p className="text-sm text-neutral-600">Loading partners — a moment.</p>
          )}

          {partners.error && !(partners.error instanceof ApiError && partners.error.status === 401) && (
            <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
              <p className="font-medium">Couldn't load partners.</p>
              <p className="mt-1 text-xs">
                Refresh the page. If the problem persists, contact your project administrator with the time and the URL.
              </p>
              <button
                type="button"
                onClick={() => partners.refetch()}
                className="mt-2 text-xs underline hover:text-rose-900"
              >Retry</button>
            </div>
          )}

          {rows.length > 0 && (
            <div className="border border-neutral-200 rounded-md overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-neutral-50 text-left text-[11px] uppercase tracking-wide text-neutral-600">
                  <tr>
                    <th scope="col" className="px-3 py-2">Partner</th>
                    <th scope="col" className="px-3 py-2">Mandate</th>
                    <th scope="col" className="px-3 py-2 text-right">Ceiling</th>
                    <th scope="col" className="px-3 py-2 text-right">Committed</th>
                    <th scope="col" className="px-3 py-2 text-right">Available</th>
                    <th scope="col" className="px-3 py-2 text-right">Projects</th>
                    <th scope="col" className="px-3 py-2">Status</th>
                    <th scope="col" className="px-3 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((p) => {
                    const fin = financeByPartner.get(p.id);
                    const ceiling = p.matching_funds_ceiling;
                    const committed = fin?.committed_in_progress_cad ?? 0;
                    const available = ceiling != null ? Math.max(0, ceiling - committed) : null;
                    const exposureStatus = fin?.status ?? 'unset';
                    const projectCount = projectCountByPartner.get(p.id) ?? 0;
                    const meta = exposureStatusMeta[exposureStatus];
                    return (
                      <tr key={p.id} className="border-t border-neutral-100 hover:bg-neutral-50/60 align-top">
                        <td className="px-3 py-2.5">
                          <div className="font-medium text-neutral-900">{p.name}</div>
                          <div className="text-[11px] text-neutral-500 mt-0.5 uppercase tracking-wide">
                            {p.partner_type.replace('_', ' ')}
                            {p.indigenous_led && (
                              <span className="ml-2 px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-900 text-[10px] normal-case tracking-normal">
                                Indigenous-led
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2.5 text-xs text-neutral-700 max-w-[26rem]">
                          <div className="line-clamp-2">{p.mandate ?? '—'}</div>
                        </td>
                        <td className="px-3 py-2.5 text-right tabular-nums">
                          {formatCADCompact(ceiling)}
                        </td>
                        <td className="px-3 py-2.5 text-right tabular-nums">
                          {formatCADCompact(committed)}
                        </td>
                        <td className={'px-3 py-2.5 text-right tabular-nums ' + moneyToneCls(available ?? 0, 'available')}>
                          {available != null ? formatCADCompact(available) : '—'}
                        </td>
                        <td className="px-3 py-2.5 text-right tabular-nums">{projectCount}</td>
                        <td className="px-3 py-2.5">
                          <span className={'inline-block text-[11px] px-1.5 py-0.5 rounded ' + meta.chipCls}>{meta.label}</span>
                        </td>
                        <td className="px-3 py-2.5 text-right">
                          <button
                            type="button"
                            onClick={() => setOpenId(p.id)}
                            className="text-xs rounded-md border border-neutral-300 px-2 py-1 hover:bg-neutral-50"
                          >Edit</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <p className="mt-3 text-[11px] text-neutral-500">
            Committed = in-progress applications (drafting · submitted · awaiting). Won/lost release the commitment. See <a href="/finance" className="underline hover:text-neutral-700">Finance</a> for the full per-partner breakdown.
          </p>
        </div>
      </div>

      <PageFooter />

      {openPartner && (
        <EditDrawer
          partner={openPartner}
          onClose={() => setOpenId(null)}
        />
      )}
    </div>
  );
}

// ── Edit drawer ──────────────────────────────────────────────────────────

interface DraftPartner {
  name: string;
  mandate: string;
  service_area: string[];
  annual_budget_cad: number | null;
  sectors: string[];
  keywords: string[];
  indigenous_led: boolean;
  geographic_catchment: string[];
  matching_funds_ceiling: number | null;
  avoid_funders: string[];
  fiscal_year_start_month: number;
}

function toDraft(p: Partner): DraftPartner {
  return {
    name: p.name,
    mandate: p.mandate ?? '',
    service_area: [...p.service_area],
    annual_budget_cad: p.annual_budget_cad,
    sectors: [...p.sectors],
    keywords: [...p.keywords],
    indigenous_led: p.indigenous_led,
    geographic_catchment: [...p.geographic_catchment],
    matching_funds_ceiling: p.matching_funds_ceiling,
    avoid_funders: [...p.avoid_funders],
    fiscal_year_start_month: p.fiscal_year_start_month,
  };
}

function EditDrawer({ partner, onClose }: { partner: Partner; onClose: () => void }) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<DraftPartner>(() => toDraft(partner));
  const [dirty, setDirty] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);

  // Update form state on partner change.
  useEffect(() => {
    setDraft(toDraft(partner));
    setDirty(false);
  }, [partner.id, partner.updated_at]);

  // Focus the first field on open.
  useEffect(() => {
    dialogRef.current?.querySelector<HTMLInputElement>('input[name="name"]')?.focus();
  }, []);

  // Esc to close.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); }
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault();
        if (dirty) save.mutate();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirty]);

  const save = useMutation({
    mutationFn: () => {
      const patch: Record<string, unknown> = {};
      if (draft.name !== partner.name) patch.name = draft.name;
      const mandate = draft.mandate.trim() || null;
      if (mandate !== partner.mandate) patch.mandate = mandate;
      if (!arrEq(draft.service_area, partner.service_area)) patch.service_area = draft.service_area;
      if (draft.annual_budget_cad !== partner.annual_budget_cad) patch.annual_budget_cad = draft.annual_budget_cad;
      if (!arrEq(draft.sectors, partner.sectors)) patch.sectors = draft.sectors;
      if (!arrEq(draft.keywords, partner.keywords)) patch.keywords = draft.keywords;
      if (draft.indigenous_led !== partner.indigenous_led) patch.indigenous_led = draft.indigenous_led;
      if (!arrEq(draft.geographic_catchment, partner.geographic_catchment)) patch.geographic_catchment = draft.geographic_catchment;
      if (draft.matching_funds_ceiling !== partner.matching_funds_ceiling) patch.matching_funds_ceiling = draft.matching_funds_ceiling;
      if (!arrEq(draft.avoid_funders, partner.avoid_funders)) patch.avoid_funders = draft.avoid_funders;
      if (draft.fiscal_year_start_month !== partner.fiscal_year_start_month) patch.fiscal_year_start_month = draft.fiscal_year_start_month;
      if (Object.keys(patch).length === 0) return Promise.resolve({ data: partner });
      return api.patch<Partner>(`/partners/${partner.id}`, patch);
    },
    onSuccess: () => {
      pushToast(`Saved ${partner.name}`, 'success', 1800);
      setDirty(false);
      qc.invalidateQueries({ queryKey: ['partners'] });
      qc.invalidateQueries({ queryKey: ['finance-exposure'] });
    },
    onError: (err) => {
      pushToast(err instanceof ApiError ? err.message : 'Save failed — keep your changes and retry', 'error');
    },
  });

  const update = <K extends keyof DraftPartner>(k: K, v: DraftPartner[K]) => {
    setDraft((d) => ({ ...d, [k]: v }));
    setDirty(true);
  };

  const formState = save.isPending ? 'saving' : dirty ? 'unsaved' : 'saved';

  return (
    <>
      {/* Backdrop — clicking dismisses, but no opacity overlay (drawer is a
          sidecar, not a modal — keeps the table visible). */}
      <div
        className="fixed inset-0 z-40 bg-black/10"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={`Edit ${partner.name}`}
        className="fixed right-0 top-0 bottom-0 z-50 w-full sm:w-[520px] bg-white border-l border-neutral-200 shadow-xl flex flex-col"
      >
        <header className="px-4 py-3 border-b border-neutral-200 flex items-center justify-between shrink-0">
          <div>
            <h2 className="text-base font-semibold text-neutral-900">{partner.name}</h2>
            <p className="text-[11px] text-neutral-500">Edit partner — changes save when you press Save.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-neutral-300 px-2 py-1 text-xs hover:bg-neutral-50"
            aria-label="Close partner editor"
          >Close <kbd className="font-mono text-[10px] bg-neutral-100 px-1 rounded ml-1">Esc</kbd></button>
        </header>

        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-5">
          <Section title="Basics">
            <Field label="Name">
              <input
                type="text"
                name="name"
                value={draft.name}
                onChange={(e) => update('name', e.target.value)}
                className="w-full border border-neutral-300 rounded-md px-2 py-1.5 text-sm"
              />
            </Field>
            <Field label="Mandate" hint="Used as context for grant-matching reasoning.">
              <textarea
                value={draft.mandate}
                onChange={(e) => update('mandate', e.target.value)}
                rows={4}
                className="w-full border border-neutral-300 rounded-md px-2 py-1.5 text-sm leading-relaxed"
              />
            </Field>
            <Field label="Indigenous-led">
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={draft.indigenous_led}
                  onChange={(e) => update('indigenous_led', e.target.checked)}
                  className="rounded"
                />
                <span className="text-xs text-neutral-700">
                  {draft.indigenous_led ? 'Yes — eligible for Indigenous-restricted grants' : 'No'}
                </span>
              </label>
            </Field>
          </Section>

          <Section title="Eligibility tags" hint="Sectors and keywords drive the matching engine. Add the words a funder would actually use.">
            <Field label="Sectors">
              <TagInput value={draft.sectors} onChange={(v) => update('sectors', v)} placeholder="add a sector…" />
            </Field>
            <Field label="Keywords">
              <TagInput value={draft.keywords} onChange={(v) => update('keywords', v)} placeholder="add a keyword…" />
            </Field>
          </Section>

          <Section title="Geographic catchment" hint="Provinces, regions, or municipalities this partner serves.">
            <Field label="Catchment">
              <TagInput value={draft.geographic_catchment} onChange={(v) => update('geographic_catchment', v)} placeholder="add a region…" />
            </Field>
            <Field label="Service area">
              <TagInput value={draft.service_area} onChange={(v) => update('service_area', v)} placeholder="add a municipality…" />
            </Field>
          </Section>

          <Section title="Finance">
            <Field label="Annual budget (CAD)">
              <MoneyInput
                value={draft.annual_budget_cad}
                onChange={(v) => update('annual_budget_cad', v)}
              />
            </Field>
            <Field label="Matching-funds ceiling (CAD)" hint="Maximum cash this partner can commit as matching funds across all active applications.">
              <MoneyInput
                value={draft.matching_funds_ceiling}
                onChange={(v) => update('matching_funds_ceiling', v)}
              />
            </Field>
            <Field label="Fiscal year start month" hint="1 = January. Drives application-year grouping.">
              <input
                type="number"
                min={1}
                max={12}
                value={draft.fiscal_year_start_month}
                onChange={(e) => update('fiscal_year_start_month', Number(e.target.value) || 1)}
                className="w-20 border border-neutral-300 rounded-md px-2 py-1.5 text-sm"
              />
            </Field>
            <Field label="Avoid funders" hint="Funders this partner has chosen not to apply to (conflict, capacity, etc.).">
              <TagInput value={draft.avoid_funders} onChange={(v) => update('avoid_funders', v)} placeholder="add a funder ID…" />
            </Field>
          </Section>
        </div>

        <footer className="px-4 py-3 border-t border-neutral-200 flex items-center justify-between shrink-0 bg-neutral-50/60">
          <span className="text-[11px] text-neutral-600" aria-live="polite">
            {formState === 'saved' && 'Saved · ' + new Date(partner.updated_at).toLocaleString('en-CA', { hour: '2-digit', minute: '2-digit' })}
            {formState === 'unsaved' && 'Unsaved changes'}
            {formState === 'saving' && 'Saving…'}
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-neutral-300 px-3 py-1.5 text-sm hover:bg-neutral-50"
            >Cancel</button>
            <button
              type="button"
              onClick={() => save.mutate()}
              disabled={!dirty || save.isPending}
              className="rounded-md bg-accent text-white px-3 py-1.5 text-sm disabled:opacity-50 hover:bg-accent-hover flex items-center gap-2"
            >
              <span>{save.isPending ? 'Saving…' : 'Save'}</span>
              <kbd className="font-mono text-[10px] bg-black/20 px-1 py-0.5 rounded">⌘⏎</kbd>
            </button>
          </div>
        </footer>
      </div>
    </>
  );
}

// ── Small form pieces ───────────────────────────────────────────────────

function Section({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3">
      <div>
        <h3 className="text-[11px] uppercase tracking-wide text-neutral-600">{title}</h3>
        {hint && <p className="text-[11px] text-neutral-500 mt-0.5">{hint}</p>}
      </div>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs text-neutral-700 mb-1">{label}</span>
      {children}
      {hint && <span className="block text-[11px] text-neutral-500 mt-1">{hint}</span>}
    </label>
  );
}

function MoneyInput({ value, onChange }: { value: number | null; onChange: (v: number | null) => void }) {
  return (
    <div className="flex items-center gap-1">
      <span className="text-neutral-500 text-sm">$</span>
      <input
        type="number"
        min={0}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
        className="flex-1 border border-neutral-300 rounded-md px-2 py-1.5 text-sm tabular-nums"
      />
    </div>
  );
}

function TagInput({ value, onChange, placeholder }: { value: string[]; onChange: (v: string[]) => void; placeholder?: string }) {
  const [draft, setDraft] = useState('');
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    if (value.includes(v)) { setDraft(''); return; }
    onChange([...value, v]);
    setDraft('');
  };
  return (
    <div className="border border-neutral-300 rounded-md px-2 py-1.5 flex flex-wrap gap-1 min-h-[2.25rem]">
      {value.map((tag) => (
        <span key={tag} className="inline-flex items-center gap-1 bg-neutral-100 text-neutral-800 text-xs px-1.5 py-0.5 rounded">
          {tag}
          <button
            type="button"
            onClick={() => onChange(value.filter((t) => t !== tag))}
            className="text-neutral-500 hover:text-neutral-900"
            aria-label={`Remove ${tag}`}
          >×</button>
        </span>
      ))}
      <input
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); add(); }
          if (e.key === 'Backspace' && draft === '' && value.length > 0) {
            onChange(value.slice(0, -1));
          }
        }}
        onBlur={add}
        placeholder={placeholder}
        className="flex-1 min-w-[120px] text-xs outline-none bg-transparent"
      />
    </div>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────────

function arrEq(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((x, i) => x === b[i]);
}

// formatCAD is imported but only used inside the rendered table indirectly
// via formatCADCompact; this keeps the unused-import lint happy if the
// linter ever runs over this file.
void formatCAD;
